import React from "react";
import {
  CalendarDays,
  Clock,
  MapPin,
  User,
  Phone,
  Send,
  Truck,
  LocateFixed,
  Crosshair,
} from "lucide-react";

const DeliveryLeftPanel = ({
  isSchedule,
  deliveryType,
  handleDeliveryTypeChange,

  getPackerSlotDetailsLoading,
  packerSlotGroups,
  selectedScheduleDate,
  setSelectedScheduleDate,
  setFormData,
  formData,
  visibleScheduleSlots,
  handleSelectScheduleSlot,
  formatScheduleDate,
  formatFullScheduleDate,

  errors,
  handleUseCurrentLocation,

  renderRouteInput,
  renderInput,

  validateForm,
  onNext,
}) => {
  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-white">
      <div className="flex-1 overflow-y-auto hide-scrollbar p-4 pb-4 sm:p-5 lg:px-7 lg:py-5">
        {/* HEADER */}
        <div className="mb-3 shrink-0">
          <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-blue-600">
            <Truck size={13} />
            Packky Delivery
          </div>

          <h3 className="text-[22px] font-bold tracking-tight text-slate-900">
            Book Your Delivery
          </h3>

          <p className="mt-0.5 text-[13px] text-slate-500">
            Add pickup, drop-off and contact details to continue.
          </p>
        </div>

        {/* DELIVERY TYPE TOGGLE */}
        <div className="mb-3 shrink-0">
          <div className="mb-1.5 flex items-center justify-between gap-3">
            <h4 className="text-sm font-bold text-slate-800">Delivery Type</h4>

            <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold text-slate-500">
              {isSchedule ? "Scheduled delivery" : "Instant delivery"}
            </span>
          </div>

          <div className="grid w-full grid-cols-2 rounded-2xl bg-slate-100 p-1">
            <button
              type="button"
              onClick={() => handleDeliveryTypeChange("instant")}
              className={`flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-bold transition-all duration-200 ${
                deliveryType === "instant"
                  ? "bg-white text-blue-700 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Truck size={16} />
              Instant
            </button>

            <button
              type="button"
              onClick={() => handleDeliveryTypeChange("schedule")}
              className={`flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-bold transition-all duration-200 ${
                deliveryType === "schedule"
                  ? "bg-white text-blue-700 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <CalendarDays size={16} />
              Schedule
            </button>
          </div>
        </div>

        {/* DATE / TIME OR INSTANT INFO */}
        <div className="mb-2 shrink-0">
          {isSchedule ? (
            <div className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50/80 p-3">
              <div className="mb-2 flex items-center justify-between gap-3">
                <div>
                  <h5 className="text-[13px] font-bold text-slate-800">
                    Schedule pickup
                  </h5>
                  <p className="text-[11px] text-slate-500">
                    Choose an available pickup slot.
                  </p>
                </div>

                {formData?.scheduleSlotTime && (
                  <span className="rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-bold text-blue-700">
                    {formData.scheduleSlotLabel}
                  </span>
                )}
              </div>

              {getPackerSlotDetailsLoading ? (
                <div className="space-y-2">
                  <div className="flex gap-2 overflow-hidden">
                    {[1, 2, 3].map((item) => (
                      <div
                        key={item}
                        className="h-8 w-24 shrink-0 animate-pulse rounded-full bg-white"
                      />
                    ))}
                  </div>

                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {[1, 2, 3, 4, 5, 6].map((item) => (
                      <div
                        key={item}
                        className="h-[58px] animate-pulse rounded-xl bg-white"
                      />
                    ))}
                  </div>
                </div>
              ) : packerSlotGroups.length > 0 ? (
                <>
                  <div
                    className="mb-2 overflow-x-scroll pb-1"
                    style={{ WebkitOverflowScrolling: "touch" }}
                  >
                    <div className="flex w-max gap-2">
                      {packerSlotGroups.map((group) => {
                        const active = selectedScheduleDate === group.date;
                        const availableCount = group.slots.filter(
                          (slot) => slot.isAvailable,
                        ).length;

                        return (
                          <button
                            key={group.date}
                            type="button"
                            onClick={() => {
                              setSelectedScheduleDate(group.date);

                              setFormData((prev) => ({
                                ...prev,
                                scheduleDate: group.date,
                                scheduleTime: "",
                                scheduleEndTime: "",
                                scheduleSlotTime: "",
                                scheduleSlotId: "",
                                scheduleSlotLabel: "",
                                packerSlotId: "",
                              }));
                            }}
                            className={`min-w-[110px] shrink-0 rounded-2xl border px-3 py-2 text-left transition-all ${
                              active
                                ? "border-blue-600 bg-blue-600 text-white shadow-md shadow-blue-100"
                                : "border-slate-200 bg-white text-slate-700 hover:border-blue-200 hover:bg-blue-50"
                            }`}
                          >
                            <p className="text-[12px] font-bold">
                              {formatScheduleDate(group.date)}
                            </p>

                            <p
                              className={`mt-0.5 text-[10px] font-semibold ${
                                active ? "text-white/80" : "text-slate-400"
                              }`}
                            >
                              {availableCount} slots available
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mb-2 rounded-xl bg-white px-3 py-2">
                    <p className="text-[11px] font-bold text-slate-700">
                      {formatFullScheduleDate(selectedScheduleDate)}
                    </p>
                    <p className="text-[10px] text-slate-400">
                      Select one time window below
                    </p>
                  </div>

                  {visibleScheduleSlots.length > 0 ? (
                    <div className="grid max-h-[150px] grid-cols-2 gap-2 overflow-y-auto hide-scrollbar pr-1 sm:grid-cols-3">
                      {visibleScheduleSlots.map((slot) => {
                        const isSelected =
                          formData?.scheduleSlotId === slot.slotId ||
                          formData?.packerSlotId === slot.slotId;

                        return (
                          <button
                            key={slot.slotId}
                            type="button"
                            disabled={!slot.isAvailable}
                            onClick={() => handleSelectScheduleSlot(slot)}
                            className={`rounded-xl border px-3 py-2 text-left transition-all ${
                              isSelected
                                ? "border-blue-600 bg-blue-600 text-white shadow-md shadow-blue-100"
                                : slot.isAvailable
                                  ? "border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50"
                                  : "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400 opacity-70"
                            }`}
                          >
                            <div className="flex items-center gap-1.5">
                              <Clock
                                size={13}
                                className={
                                  isSelected ? "text-white" : "text-blue-500"
                                }
                              />

                              <span className="truncate text-[12px] font-bold">
                                {slot.label}
                              </span>
                            </div>

                            <p
                              className={`mt-1 text-[10px] font-semibold ${
                                isSelected ? "text-white/80" : "text-slate-400"
                              }`}
                            >
                              {slot.isAvailable ? "Available" : "Unavailable"}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex h-[74px] items-center justify-center rounded-xl border border-dashed border-slate-200 bg-white text-center">
                      <p className="text-[12px] font-medium text-slate-500">
                        No slots found for this date.
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex h-[90px] items-center justify-center rounded-xl border border-dashed border-slate-200 bg-white text-center">
                  <p className="text-[12px] font-medium text-slate-500">
                    No delivery slots available right now.
                  </p>
                </div>
              )}

              <div className="min-h-[14px]">
                {errors?.scheduleSlotId && (
                  <p className="mt-1 text-[11px] font-medium text-red-500">
                    {errors.scheduleSlotId}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="flex h-full items-center gap-3 rounded-2xl border border-blue-100 bg-gradient-to-r from-blue-50 to-slate-50 px-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-blue-600 shadow-sm">
                <Truck size={19} />
              </div>

              <div className="min-w-0">
                <h5 className="text-sm font-bold text-slate-800">
                  Instant delivery selected
                </h5>
                <p className="mt-0.5 text-[12px] leading-5 text-slate-500">
                  Your driver will be assigned as soon as you confirm the
                  booking.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* ROUTE */}
        <div className="mb-2 shrink-0">
          <div className="mb-1.5 flex items-center justify-between">
            <h4 className="text-sm font-bold text-slate-800">Route</h4>

            <button
              type="button"
              onClick={handleUseCurrentLocation}
              className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-[11px] font-bold text-blue-700 transition hover:bg-blue-100"
            >
              <Crosshair size={12} />
              Use current
            </button>
          </div>

          <div className="relative rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-2.5">
            <div className="pointer-events-none absolute left-[35px] top-[44px] bottom-[44px] border-l-2 border-dashed border-blue-200" />

            {renderRouteInput({
              title: "From",
              placeholder: "Search pickup location",
              icon: LocateFixed,
              name: "pickupAddress",
            })}

            <div className="my-1.5 border-t border-slate-200" />

            {renderRouteInput({
              title: "To",
              placeholder: "Search drop-off location",
              icon: MapPin,
              name: "dropAddress",
            })}
          </div>
        </div>

        {/* SENDER */}
        <div className="mb-1 shrink-0">
          <h4 className="mb-1.5 text-sm font-bold text-slate-800">
            Sender Details
          </h4>

          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {renderInput({
              label: "Your Name",
              icon: User,
              placeholder: "Enter your name",
              name: "senderName",
            })}

            {renderInput({
              label: "Mobile Number",
              icon: Phone,
              placeholder: "Enter mobile number",
              name: "senderMobile",
            })}
          </div>
        </div>

        {/* RECEIVER */}
        <div className="mb-2 shrink-0">
          <h4 className="mb-1.5 text-sm font-bold text-slate-800">
            Receiver's Details
          </h4>

          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {renderInput({
              label: "Receiver's Name",
              icon: User,
              placeholder: "Enter receiver name",
              name: "receiverName",
            })}

            {renderInput({
              label: "Mobile Number",
              icon: Phone,
              placeholder: "Enter mobile number",
              name: "receiverMobile",
            })}
          </div>
        </div>

        {/* SUBMIT */}
        <button
          type="button"
          onClick={() => {
            if (validateForm()) {
              onNext();
            }
          }}
          className="group mt-auto flex w-full shrink-0 items-center justify-center gap-2 rounded-2xl bg-[#1f2d3f] px-5 py-3 text-sm font-bold text-white shadow-lg shadow-slate-300 transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#172334] hover:shadow-xl active:translate-y-0"
        >
          <Send size={17} className="transition group-hover:translate-x-0.5" />
          Driver is on the Way
        </button>
      </div>
    </div>
  );
};

export default DeliveryLeftPanel;
